import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
	getAccounts,
	updateAccount as updateAccountApi,
    removeAccount as removeAccountApi,
    addAccount as addAccountApi,
    addAccountNote as addAccountNoteApi
} from '@/api/account'
import IApiResponse from '@/types/api-response'
import IList from '@/types/list-result'
import IPagination from '@/types/pagination'
import { SiteCode } from '@/configs/enums'

interface AccountState {
	accounts: IAccount[]
	loading: boolean
	error: string | null
	pagination?: IPagination

	updating: boolean
}

export interface IAccount {
	_id: string
	username: string
	display_name?: string | null
	account_type: string
	password?: string | null
	third_party_user_id?: string | null
	cookies_updated?: Date | null
	cookies: any
	logged_in: boolean
	is_active: boolean
	transport_methods: string[] | null
	site_code: SiteCode
	review_rate: number
	auto_sync_review_rate: boolean
    last_sync_review_rate: Date | null
	payment_cards: Array<{
		active: boolean
		card_end: string
		credit_id: string
	}>
	max_number_of_bid: number
	min_price_per_product: number
	max_price_per_product: number
	paypay_priority: boolean
	fetch_success_at: Date | null
	notice_logged_out: boolean
	group_id: string | null
	notes: Array<{
        _id: string
		full_name: string
		user_id: string
		created_at: string
		note: string
        sub_id?: string
	}>
	created_at: Date
	updated_at: Date | null,
    system_code: Array<string>
}

const initialState: AccountState = {
	loading: false,
	accounts: [],
	error: null,

	updating: false,
}

export const fetchAccounts = createAsyncThunk<
	IApiResponse<IList<IAccount>>,
	any
>('account/fetch_accounts', async (params: any) => {
	return await getAccounts(params)
})

export const updateAccount = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('account/update_account', async ({ params, cb }: any) => {
	try {
		const result = await updateAccountApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

export const removeAccount = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('account/remove_account', async ({ params, cb }: any) => {
	try {
		const result = await removeAccountApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

export const addAccount = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('account/add_account', async ({ params, cb }: any) => {
	try {
		const result = await addAccountApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

export const addNote = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('account/add_account_note', async ({ params, cb }: any) => {
	try {
		const result = await addAccountNoteApi(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error) {
		if (typeof cb === 'function') {
			cb({ success: false })
		}

		throw error
	}
})

const accountSlice = createSlice({
	name: 'account',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAccounts.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchAccounts.fulfilled, (state, { payload }) => {
				state.loading = false
				state.pagination = payload.data!.pagination
				if (payload.success) {
					state.accounts = payload.data!.records
				} else {
					state.accounts = []
				}
			})
			.addCase(fetchAccounts.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message || 'Something went wrong'
			})

			.addCase(updateAccount.pending, (state) => {
				state.updating = true
			})
			.addCase(updateAccount.fulfilled, (state) => {
				state.updating = false
			})
			.addCase(updateAccount.rejected, (state, action) => {
				state.updating = false
				state.error = action.error.message || 'Something went wrong'
			})
	},
})

export default accountSlice.reducer
